import { Grid2 as Grid, FormControlLabel, Switch, Typography, MenuItem } from "@mui/material"
import Button from "@mui/material/Button"
import { Field, Formik } from "formik"
import { useMemo } from "react"
import * as yup from "yup"
import { Revisione } from "../../types"
import { transformErrorForForm } from "../../utils/utils"
import { FormInDialogProps } from "../DialogForm"
import { FormikDatePicker, FormikSelect, FormikTextField } from "./formsHelpers"
import { useTranslation } from "react-i18next"
import { ACAssemblaggio } from "./autocompletes"

type SelectStatoFisicoProps = {
  name: string
  label: string
  margin?: "none" | "dense" | "normal" | undefined
  variant?: "standard" | "outlined" | "filled" | undefined
  fullWidth?: boolean
  size?: "small" | "medium" | undefined
}

export function SelectStatoFisico({
  name,
  label,
  margin = "dense",
  variant = "outlined",
  fullWidth = true,
  ...props
}: SelectStatoFisicoProps) {
  const { t } = useTranslation()
  return (
    <Field
      component={FormikSelect}
      name={name}
      label={label}
      margin={margin}
      fullWidth={fullWidth}
      variant={variant}
      {...props}
    >
      <MenuItem value={0}>{t("revisione.stato_fisico_enum.assenti")}</MenuItem>
      <MenuItem value={1}>{t("revisione.stato_fisico_enum.lievi")}</MenuItem>
      <MenuItem value={2}>{t("revisione.stato_fisico_enum.medie")}</MenuItem>
      <MenuItem value={3}>{t("revisione.stato_fisico_enum.forti")}</MenuItem>
      <MenuItem value={4}>{t("revisione.stato_fisico_enum.gravi")}</MenuItem>
    </Field>
  )
}

export default function RevisioneFileForm({ item, save, ref }: FormInDialogProps<Revisione>) {
  const { t } = useTranslation()
  const initialValues = useMemo(() => {
    return (
      item ?? {
        data: "",
        supporto: null,
        assemblato: false,
        assemblaggio: undefined,
        note: "",
        giunte_colla: 0,
        giunte_nastro: 0,
        giunte_colla_rip: 0,
        giunte_nastro_rip: 0,
        giunte_rinforzate: 0,
        bruciature: 0,
        aceto: false,
        graffi: 0,
        polvere: 0,
        imbarcatura: 0,
        piegatura: 0,
        ondulazione: 0,
        restringimento: 0,
        curling: 0,
        colore: "",
        note_rev: "",
        alterdim: false,
        proiettabile: false,
        pulizia: false,
        esterna: false,
        lab: "",
        adstrip: 0,
        luogo: "",
        uscita: "",
        rientro: "",
      }
    )
  }, [item])

  const schema = useMemo(() => yup.object().shape({}), [t])

  return (
    <Formik
      validationSchema={schema}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values, formikBag) => {
        return save(values as Revisione).catch((e) => {
          const localError = transformErrorForForm(e)
          formikBag.setErrors(localError)
        })
      }}
    >
      {({ handleSubmit, values, setFieldValue }) => (
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={1} sx={{ py: 2 }}>
            <Grid size={4}>
              <Field
                component={FormikDatePicker}
                name="data"
                label={t("revisione.data")}
                required
                margin="dense"
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid size={4} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FormControlLabel
                control={
                  <Switch checked={values.assemblato} onChange={(e) => setFieldValue("assemblato", e.target.checked)} />
                }
                label={t("revisione.assemblato")}
              />
            </Grid>
            <Grid size={4}>
              <ACAssemblaggio
                label={t("revisione.nome_assemblaggio")}
                margin="dense"
                variant="outlined"
                fullWidth
                value={values.assemblaggio}
                onChange={(value) => {
                  setFieldValue("assemblaggio", value)
                }}
              />
            </Grid>

            <Grid size={12}>
              <Field
                name="note"
                label={t("revisione.note")}
                component={FormikTextField}
                margin="dense"
                variant="outlined"
                fullWidth
                multiline
                minRows={4}
              />
            </Grid>

            <Grid size={12}>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {t("revisione.stato_fisico_film")}
              </Typography>
            </Grid>

            <Grid size={4}>
              <Field
                component={FormikSelect}
                name="colore"
                label={t("revisione.colore")}
                margin="dense"
                fullWidth
                variant="outlined"
              >
                <MenuItem value="">-</MenuItem>
                <MenuItem value="originale">{t("revisione.colore_enum.originale")}</MenuItem>
                <MenuItem value="decaduto">{t("revisione.colore_enum.decaduto")}</MenuItem>
              </Field>

              <FormControlLabel
                control={<Switch checked={values.aceto} onChange={(e) => setFieldValue("aceto", e.target.checked)} />}
                label={t("revisione.aceto")}
              />

              <Field
                component={FormikSelect}
                name={"adstrip"}
                label={t("revisione.adstrip")}
                margin="dense"
                fullWidth
                variant="outlined"
              >
                <MenuItem value={0}>{t("revisione.adstrip_enum.buono")}</MenuItem>
                <MenuItem value={1}>{t("revisione.adstrip_enum.abbastanza_buono")}</MenuItem>
                <MenuItem value={1.5}>{t("revisione.adstrip_enum.inizio_decadimento")}</MenuItem>
                <MenuItem value={2}>{t("revisione.adstrip_enum.deterioramento_rapido")}</MenuItem>
                <MenuItem value={3}>{t("revisione.adstrip_enum.situazione_critica")}</MenuItem>
              </Field>
            </Grid>

            <Grid size={4}>
              <SelectStatoFisico name="polvere" label={t("revisione.polvere")} />
              <SelectStatoFisico name="graffi" label={t("revisione.graffi")} />
              <SelectStatoFisico name="bruciature" label={t("revisione.bruciature")} />
            </Grid>

            <Grid size={4}>
              <SelectStatoFisico name="imbarcatura" label={t("revisione.imbarcatura")} />
              <SelectStatoFisico name="piegatura" label={t("revisione.piegatura")} />
              <SelectStatoFisico name="ondulazione" label={t("revisione.ondulazione")} />
              <SelectStatoFisico name="restringimento" label={t("revisione.restringimento")} />
              <SelectStatoFisico name="curling" label={t("revisione.curling")} />
            </Grid>

            {/* <Grid size={4} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FormControlLabel
                control={
                  <Switch checked={values.alterdim} onChange={(e) => setFieldValue("alterdim", e.target.checked)} />
                }
                label={t("revisione.alterdim")}
              />
            </Grid> */}

            <Grid size={12}>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {t("revisione.interventi_ripristino")}
              </Typography>
            </Grid>

            <Grid size={2}>
              <Field
                name="giunte_colla"
                label={t("revisione.giunte_colla")}
                component={FormikTextField}
                type="number"
                min={0}
                max={100}
                margin="dense"
                variant="outlined"
                fullWidth
              />
            </Grid>

            <Grid size={2}>
              <Field
                name="giunte_colla_rip"
                label={t("revisione.giunte_colla_rip")}
                component={FormikTextField}
                type="number"
                min={0}
                max={100}
                margin="dense"
                variant="outlined"
                fullWidth
              />
            </Grid>

            <Grid size={2}>
              <Field
                name="giunte_nastro"
                label={t("revisione.giunte_nastro")}
                component={FormikTextField}
                type="number"
                min={0}
                max={100}
                margin="dense"
                variant="outlined"
                fullWidth
              />
            </Grid>

            <Grid size={2}>
              <Field
                name="giunte_nastro_rip"
                label={t("revisione.giunte_nastro_rip")}
                component={FormikTextField}
                type="number"
                min={0}
                max={100}
                margin="dense"
                variant="outlined"
                fullWidth
              />
            </Grid>
            <Grid size={2}>
              <Field
                name="giunte_rinforzate"
                label={t("revisione.giunte_rinforzate")}
                component={FormikTextField}
                type="number"
                min={0}
                max={100}
                margin="dense"
                variant="outlined"
                fullWidth
              />
            </Grid>

            <Grid size={3} sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.proiettabile}
                    onChange={(e) => setFieldValue("proiettabile", e.target.checked)}
                  />
                }
                label={t("revisione.proiettabile")}
              />
            </Grid>

            <Grid size={3} sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch checked={values.pulizia} onChange={(e) => setFieldValue("pulizia", e.target.checked)} />
                }
                label={t("revisione.pulizia")}
              />
            </Grid>
            <Grid size={6}></Grid>

            {/* <Grid size={4}>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {t("revisione.revisione_esterna")}
              </Typography>
            </Grid> */}

            <Grid size={2} sx={{ display: "flex", alignItems: "center", justifyContent: "start" }}>
              <FormControlLabel
                control={
                  <Switch checked={values.esterna} onChange={(e) => setFieldValue("esterna", e.target.checked)} />
                }
                label={t("revisione.esterna")}
              />
            </Grid>

            {values.esterna && (
              <>
                <Grid size={6}>
                  <Field
                    name="lab"
                    disabled={!values.esterna}
                    label={t("revisione.lab")}
                    component={FormikTextField}
                    margin="dense"
                    variant="outlined"
                    fullWidth
                  />
                </Grid>

                <Grid size={4}>
                  <Field
                    name="luogo"
                    disabled={!values.esterna}
                    label={t("revisione.luogo")}
                    component={FormikTextField}
                    margin="dense"
                    variant="outlined"
                    fullWidth
                  />
                </Grid>

                <Grid size={12}>
                  <Field
                    name="note_rev"
                    disabled={!values.esterna}
                    label={t("revisione.note_rev_esterna")}
                    component={FormikTextField}
                    margin="dense"
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={3}
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    component={FormikDatePicker}
                    name="uscita"
                    disabled={!values.esterna}
                    label={t("revisione.uscita")}
                    required
                    margin="dense"
                    fullWidth
                    variant="outlined"
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    component={FormikDatePicker}
                    name="rientro"
                    disabled={!values.esterna}
                    label={t("revisione.rientro")}
                    required
                    margin="dense"
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Button ref={ref} type="submit" color="primary" sx={{ display: "none" }}></Button>
        </form>
      )}
    </Formik>
  )
}
